import { useStore } from '@nanostores/react';
import { json, redirect, type LoaderFunctionArgs, type TypedResponse } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import { atom } from 'nanostores';
import { useEffect, type ReactNode } from 'react';
import { ClientOnly } from 'remix-utils/client-only';

import { LightRays } from '~/components/ui/LightRays.client';
import boltLogo from '~/images/bolt-connect.svg';
import netlifyLogo from '~/images/netlify-connect.svg';
import { IndexLayout } from '~/layouts/IndexLayout';
import { isApplicationProvider } from '~/lib/.server/applications';
import { handleWithAuth } from '~/lib/.server/auth-middleware';
import { withUserData } from '~/lib/.server/user';
import { applicationManager, type ApplicationProvider } from '~/lib/stores/applicationManager';
import { classNames } from '~/utils/classNames';
import { withSpinner } from '~/utils/withSpinner';

interface ConnectLoaderData {
  loggedIn: boolean;
  provider: ApplicationProvider;
}

export const APPLICATIONS = new Map<
  ApplicationProvider,
  {
    name: string;
    logo: ReactNode;
    type?: string;
    styles?: string;
    warning?: { disconnect?: string };
    description: ReactNode;
    hidden?: boolean;
  }
>([
  [
    'SUPABASE',
    {
      name: 'Supabase',
      type: 'Database',
      logo: <div className="i-logos:supabase-icon size-6" />,
      styles: 'p-6',
      description:
        'Integrate Supabase to enable authentication or sync your app with a robust and scalable database effortlessly.',
    },
  ],
  [
    'NETLIFY',
    {
      name: 'Netlify',
      type: 'Hosting',
      logo: <img src={netlifyLogo} className="size-6" alt="Netlify" />,
      styles: 'bg-brands-netlify-background',
      description:
        'Deploy your app seamlessly with your own Netlify account. Use custom domains, optimize performance, and take advantage of powerful deployment tools.',
    },
  ],
  [
    'FIGMA',
    {
      name: 'Figma',
      type: 'Design',
      logo: <div className="i-logos:figma size-6" />,
      styles: 'p-6',
      description: 'Integrate Figma to import your designs as code ready to be analyzed by Bolt.',
    },
  ],
]);

export async function loader(args: LoaderFunctionArgs) {
  const provider = args.params.provider?.toUpperCase();

  if (!isApplicationProvider(provider)) {
    return redirect('/');
  }

  return handleWithAuth<LoaderFunctionArgs, TypedResponse<ConnectLoaderData>>(
    args,
    async (_args, session) => {
      const userData = await withUserData({}, session, args.request);

      return json({ loggedIn: true, provider, ...userData });
    },
    (response) => json({ loggedIn: false, provider }, response),
  );
}

let isChecked = false;

const state = atom({ isConnecting: true, error: null, countdown: 5 });

async function connectWithProvider(provider: ApplicationProvider) {
  if (isChecked) {
    return;
  }

  isChecked = true;

  try {
    await withSpinner({
      action: () => {
        return applicationManager.onConnect(provider);
      },
      onStateUpdate: (isSpinning) => {
        state.set({ ...state.get(), isConnecting: isSpinning });

        if (!isSpinning) {
          const interval = setInterval(() => {
            const { countdown } = state.get();

            if (countdown - 1 < 1) {
              clearInterval(interval);

              window.close();
            }

            state.set({ ...state.get(), countdown: countdown - 1 });
          }, 1000);
        }
      },
      spinnerDelay: 0,
      minDuration: 500,
    });
  } catch (error: any) {
    state.set({ ...state.get(), isConnecting: false, error: error.message });
  }
}

export default function Index() {
  const { provider } = useLoaderData<typeof loader>();

  const { isConnecting, error, countdown } = useStore(state);

  const { name: applicationName, logo: applicationLogo, styles: applicationStyles } = APPLICATIONS.get(provider)!;

  useEffect(() => {
    connectWithProvider(provider);
  }, []);

  return (
    <IndexLayout>
      <ClientOnly>{() => <LightRays />}</ClientOnly>

      <div className="flex flex-col items-center justify-center h-screen">
        <div className="w-lg bg-bolt-elements-background-depth-2 rounded-2xl p-15 pb-25 pt-25 border border-bolt-elements-borderColor border-1">
          <div className="flex justify-center mb-2">
            <span className="size-22 flex flex-items-center justify-center b-1 rounded-2xl border-bolt-elements-borderColor bg-gray-900">
              <img src={boltLogo} alt="Bolt" />
            </span>

            <div className="mx-6 flex items-center justify-center">
              <div
                className={classNames(
                  'w-10 h-10 bg-bolt-elements-background-depth-3 rounded-full flex items-center justify-center',
                  {
                    'bg-emerald-500 bg-opacity-10': !isConnecting && !error,
                    'bg-red-500 bg-opacity-10': !isConnecting && !!error,
                  },
                )}
              >
                {isConnecting ? (
                  <div className="i-svg-spinners:90-ring-with-bg scale-150" />
                ) : error ? (
                  <span className="i-ph-x-circle-light text-3xl text-red-500" />
                ) : (
                  <span className="i-ph-check-circle-light text-3xl text-emerald-500" />
                )}
              </div>
            </div>

            <div className="bg-gray-900 rounded-2xl b-1 overflow-hidden border-bolt-elements-borderColor">
              <span className={'size-22 flex flex-items-center justify-center ' + applicationStyles}>
                {applicationLogo}
              </span>
            </div>
          </div>

          <h1 className="text-xl mb-4 text-center mt-4 text-bolt-elements-text-primary">
            {isConnecting
              ? 'Connecting Bolt with ' + applicationName
              : error
                ? 'Failed to connect with ' + applicationName
                : applicationName + ' successfully connected'}
          </h1>

          <p className="text-center text-bolt-elements-text-secondary">
            {error ||
              (isConnecting
                ? 'Please keep this tab open while we establish a connection to your environment.'
                : `You can close this window, or the window will automatically close in ${countdown} second${countdown === 1 ? '' : 's'}.`)}
          </p>
        </div>
      </div>
    </IndexLayout>
  );
}
